/**
 * Dashboard Notifications tab: list, filter, search, mark read, modal (BooksyAppExpo-style).
 */
import React, { useState, useMemo, memo } from 'react';
import { FaBell, FaSyncAlt, FaCheckCircle, FaTrash, FaEnvelope, FaTimes, FaSearch } from 'react-icons/fa';
import { useI18n } from '../../contexts/I18n';
import { Card } from './Card';

const NotificationsTab = memo(function NotificationsTab({
  list,
  onClear,
  onMarkAllRead,
  onMarkRead,
  onRefresh,
  isRefreshing,
  selectedNotification,
  onNotificationSelect,
}) {
  const { t, lang } = useI18n();
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredNotifications = useMemo(() => {
    let filtered = list;
    if (filter === 'unread') filtered = filtered.filter(n => !n.read);
    else if (filter === 'read') filtered = filtered.filter(n => n.read);
    if (searchQuery) {
      filtered = filtered.filter(n =>
        n.text?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        n.title?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    return filtered.sort((a, b) => new Date(b.ts || 0) - new Date(a.ts || 0));
  }, [list, filter, searchQuery]);

  const stats = useMemo(() => {
    const total = list.length;
    const unread = list.filter(n => !n.read).length;
    const read = list.filter(n => n.read).length;
    return { total, unread, read };
  }, [list]);

  return (
    <Card extraClass={`notifications-card ${lang === 'ar' ? 'rtl' : ''}`}>
      <div className={`notifications-header ${lang === 'ar' ? 'rtl' : ''}`}>
        <div className={`notifications-header-content ${lang === 'ar' ? 'rtl' : ''}`}>
          <h2 className={`notifications-title notifications-title-row ${lang === 'ar' ? 'rtl' : ''}`}>
            <span className="tab-title-icon-wrap" aria-hidden="true">
              <FaBell className="notifications-title-icon" size={18} />
            </span>
            {t('navNotifications')}
          </h2>
          <p className={`notifications-subtitle ${lang === 'ar' ? 'rtl' : ''}`}>
            {t('dashHistorySub') || 'Stay updated with your booking'}
          </p>
        </div>
        <div className="notifications-kpi-grid">
          <div className="notifications-kpi-card notifications-kpi-primary">
            <div className="notifications-kpi-icon-wrapper"><FaBell size={18} color="#18442A" /></div>
            <div className="notifications-kpi-content">
              <div className="notifications-kpi-value">{stats.total}</div>
              <div className="notifications-kpi-label">{t('dashStatTotal') || 'Total'}</div>
            </div>
          </div>
          <div className="notifications-kpi-card notifications-kpi-warning">
            <div className="notifications-kpi-icon-wrapper"><FaEnvelope size={18} color="#b45309" /></div>
            <div className="notifications-kpi-content">
              <div className="notifications-kpi-value notifications-kpi-value--warn">{stats.unread}</div>
              <div className="notifications-kpi-label">{t('notificationUnread') || 'Unread'}</div>
            </div>
          </div>
          <div className="notifications-kpi-card notifications-kpi-success">
            <div className="notifications-kpi-icon-wrapper"><FaCheckCircle size={18} color="#047857" /></div>
            <div className="notifications-kpi-content">
              <div className="notifications-kpi-value notifications-kpi-value--ok">{stats.read}</div>
              <div className="notifications-kpi-label">{t('notificationRead') || 'Read'}</div>
            </div>
          </div>
        </div>
        <div className="notifications-actions" role="group" aria-label={t('notificationActions') || 'Notification actions'}>
          <button type="button" className={`notification-action-button ${isRefreshing ? 'notification-action-button-refreshing' : ''}`} onClick={onRefresh} disabled={isRefreshing} aria-label={t('refresh') || 'Refresh notifications'} aria-busy={isRefreshing} title={t('refresh') || 'Refresh'}>
            <FaSyncAlt size={16} />
            <span className="notification-action-button-text">{t('refresh') || 'Refresh'}</span>
          </button>
          <button type="button" className="notification-action-button" onClick={onMarkAllRead} disabled={list.length === 0} aria-label={t('markAllRead') || 'Mark all as read'}>
            <FaCheckCircle size={16} />
            <span className="notification-action-button-text">{t('markAllRead') || 'Mark All Read'}</span>
          </button>
          <button type="button" className="notification-action-button notification-action-button-danger" onClick={onClear} disabled={list.length === 0} aria-label={t('clearAll') || 'Clear all notifications'}>
            <FaTrash size={16} />
            <span className="notification-action-button-text notification-action-button-text-danger">{t('clearAll') || 'Clear All'}</span>
          </button>
        </div>
      </div>

      {list.length > 0 && (
        <div className="notifications-filters" role="search">
          <div className="notifications-filters-row">
            <div className="search-box">
              <FaSearch className="search-icon" size={16} aria-hidden="true" />
              <input type="search" placeholder={t('searchNotifications') || 'Search notifications...'} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="search-input" aria-label={t('searchNotifications') || 'Search notifications'} />
            </div>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="filter-select notifications-filter-select" aria-label={t('filterNotifications') || 'Filter by status'}>
              <option value="all">All</option>
              <option value="unread">{t('notificationUnread') || 'Unread'}</option>
              <option value="read">{t('notificationRead') || 'Read'}</option>
            </select>
          </div>
        </div>
      )}

      {list.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🔔</div>
          <h3>{t('dashNoneFound')}</h3>
          <p>{t('dashTryAdjust')}</p>
        </div>
      ) : filteredNotifications.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🔍</div>
          <h3>{t('dashNoneFound')}</h3>
          <p>{t('dashTryAdjust')}</p>
        </div>
      ) : (
        <div className="notifications-list" role="list" aria-label={t('notificationList') || 'Notifications list'}>
          {filteredNotifications.map((n) => {
            const isRead = n.read;
            return (
              <div
                key={n.id}
                role="button"
                tabIndex={0}
                className={`notification-item ${isRead ? 'notification-item-read' : 'notification-item-unread'}`}
                onClick={async (e) => { e.stopPropagation(); if (!isRead && onMarkRead) await onMarkRead(n.id); }}
                onKeyDown={async (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!isRead && onMarkRead) await onMarkRead(n.id); } }}
                aria-label={n.title || n.text || 'Notification'}
              >
                <div className={`notification-indicator ${isRead ? 'notification-indicator--read' : 'notification-indicator--unread'}`} aria-hidden="true">
                  {isRead ? <FaCheckCircle size={18} /> : <FaBell size={18} />}
                </div>
                <div className="notification-content">
                  {n.title && <div className={`notification-title ${lang === 'ar' ? 'rtl' : ''}`}>{n.title}</div>}
                  <div className={`notification-text ${lang === 'ar' ? 'rtl' : ''}`}>{n.text}</div>
                  <div className={`notification-meta-row ${lang === 'ar' ? 'rtl' : ''}`}>
                    <div className={`notification-status-badge ${isRead ? 'notification-status-badge-read' : 'notification-status-badge-unread'}`}>
                      <span className={`notification-status-text ${isRead ? 'notification-status-text-read' : 'notification-status-text-unread'}`}>
                        {isRead ? (t('notificationRead') || 'Read') : (t('notificationUnread') || 'Unread')}
                      </span>
                    </div>
                    <div className={`notification-time ${lang === 'ar' ? 'rtl' : ''}`}>
                      {n.ts ? new Date(n.ts).toLocaleString('en-US') : '—'}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedNotification && (
        <div className="notification-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="notification-modal-title" onClick={() => onNotificationSelect?.(null)}>
          <div className="notification-modal" onClick={(e) => e.stopPropagation()}>
            <div className="notification-modal-header">
              <h3 id="notification-modal-title" className={`notification-modal-title ${lang === 'ar' ? 'rtl' : ''}`}>
                {selectedNotification.title || t('notification') || 'Notification'}
              </h3>
              <button type="button" className="notification-modal-close" onClick={() => onNotificationSelect?.(null)} aria-label={t('close') || 'Close'}><FaTimes size={20} /></button>
            </div>
            <div className={`notification-modal-body ${lang === 'ar' ? 'rtl' : ''}`}>
              <p className="notification-modal-text">{selectedNotification.text || selectedNotification.title || '—'}</p>
              <p className="notification-modal-time">{selectedNotification.ts ? new Date(selectedNotification.ts).toLocaleString('en-US') : '—'}</p>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
});

export default NotificationsTab;
